import { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Modal, Button } from '@hotjar/hotsauce';
import { useJoinOrganizationSuggestion } from '@hotjar/webapp/hooks/useJoinOrganizationSuggestion';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';

export const JoinOrganizationModal = (): JSX.Element => {
  const { t } = useTranslation();
  const { click: trackClick } = useTrack();
  const { data: joinTeamSuggestion, isLoading } = useJoinOrganizationSuggestion();
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (!isLoading && joinTeamSuggestion?.suggestJoiningOrganization) {
      setModalOpen(true);
    }
  }, [joinTeamSuggestion, isLoading, setModalOpen]);

  const onExpandClicked = () => {
    trackClick('Expand join organization suggestion', 'Join organization modal', {});
    setIsExpanded(true);
  };

  const onDismissClicked = () => {
    trackClick('Dismiss join organization suggestion', 'Join organization modal', {});
    setModalOpen(false);
  };

  const onCreateNewAccountClicked = () => {
    trackClick('Create new account', 'Join organization modal', {});
    setModalOpen(false);
  };

  const onCloseClicked = () => {
    trackClick('Close modal', 'Join organization modal', {});
    setModalOpen(false);
  };

  return (
    <Modal
      contentLabel={t('activation:onboarding.join-organization.header')}
      shouldCloseOnOverlayClick={false}
      isOpen={isModalOpen}
      onClose={onCloseClicked}
    >
      <Modal.Header title={t('activation:onboarding.join-organization.header')} />
      <Modal.Body>
        {!isExpanded && (
          <p data-testid="onboarding-with-org-suggestion">
            <Trans
              i18nKey="activation:onboarding.join-organization.prompt"
              values={{ organizationName: joinTeamSuggestion?.name }}
            >
              Looks like <b>{joinTeamSuggestion?.name}</b> is already active on Hotjar. How would
              you like to sign up?
            </Trans>
          </p>
        )}

        {isExpanded && (
          <p data-testid="onboarding-with-org-suggestion-expanded">
            <Trans
              i18nKey="activation:onboarding.join-organization.expanded-prompt"
              values={{ organizationName: joinTeamSuggestion?.name }}
            >
              To join <b>{joinTeamSuggestion?.name}</b> on Hotjar you’ll need to request an invite
              from an account admin. For security reasons we can’t help with that, but we’ll look
              forward to welcoming you when you’ve got your invite!
            </Trans>
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!isExpanded && (
          <>
            <Button
              kind="ghost"
              onClick={onCreateNewAccountClicked}
              data-testid="create-new-account"
            >
              {t('activation:onboarding.join-organization.buttons.create-new-account')}
            </Button>
            <Button kind="secondary" onClick={onExpandClicked} data-testid="expand">
              {t('activation:onboarding.join-organization.buttons.join-existing-account')}
            </Button>
          </>
        )}
        {isExpanded && (
          <Button kind="secondary" onClick={onDismissClicked} data-testid="dismiss">
            {t('activation:onboarding.join-organization.buttons.dismiss')}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
